import React, { Component } from "react";
import PropTypes from "prop-types";

import { TableCell, TableRow } from "@material-ui/core";

import MUIDataTable from "mui-datatables";

import DashboardComponent, { ComponentContext } from "../DashboardComponent";
import moment from "moment";

import { doubleClickSpeed } from "../../helpers/DashboardHelpers";
import { DataContext } from "../DataContext";

const timeFormat = "MM-DD-YY HH:mm:ss";

const columns = [
  "ID",
  "Time",
  "Location",
  "Latitude",
  "Longitude",
  "Unit",
  "Assigned",
  "Responding",
  "Arrived",
  "Scene Cleared",
  "Turnout",
  "Travel",
  "Time On Scene",
  "Committed Hours",
  {
    name: "Feature",
    options: {
      display: "excluded",
      download: false
    }
  }
];

export default class ChartDetails extends Component {
  render = () => {
    return (
      <DataContext.Consumer>
        {({ ...dataSettings }) => (
          <ComponentContext.Consumer>
            {({ ...settings }) => (
              <ChartDetailsComponent
                {...dataSettings}
                {...settings}
                {...this.props}
              />
            )}
          </ComponentContext.Consumer>
        )}
      </DataContext.Consumer>
    );
  };
}

function ChartDetailsRow(props) {
  const {
    onRowClick,
    id,
    time,
    location,
    latitude,
    longitude,
    unit,
    assigned,
    responseTime,
    turnoutTime,
    travelTime,
    arrivalTime,
    sceneCleared,
    timeOnScene,
    committedHours
  } = props;

  return (
    <TableRow hover key={id}>
      <TableCell size="small">{id}</TableCell>
      <TableCell size="small">{time}</TableCell>
      <TableCell size="small">{location}</TableCell>
      <TableCell size="small">{latitude}</TableCell>
      <TableCell size="small">{longitude}</TableCell>
      <TableCell size="small">{unit}</TableCell>
      <TableCell size="small">{assigned}</TableCell>
      <TableCell size="small">{responseTime}</TableCell>
      <TableCell size="small">{arrivalTime}</TableCell>
      <TableCell size="small">{sceneCleared}</TableCell>
      <TableCell size="small">{turnoutTime}</TableCell>
      <TableCell size="small">{travelTime}</TableCell>
      <TableCell size="small">{timeOnScene}</TableCell>
      <TableCell size="small">{committedHours}</TableCell>
    </TableRow>
  );
}

export class ChartDetailsComponent extends Component {
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();
  }

  state = {
    data: [],
    title: "Displayed Responses"
  };

  componentDidMount = () => {
    this.updateData();
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (this.props.incidents !== prevProps.incidents) {
      this.updateData();
    }
  };

  componentWillUnmount = () => {};

  updateData = () => {
    let incidents, title;

    incidents = this.props.incidents || [];
    title = "Displayed Responses";

    let data = [];
    let datum, unit, response, benchmarks;

    var renderTime = moment.now();
    let focusId, focusIndex;
    if (renderTime - this.lastClickTime < doubleClickSpeed) {
      focusId = this.clickedData[0];
    }

    let nextDataIndex = 0;
    for (var i = 0; i < incidents.length; i++) {
      datum = incidents[i];
      if (!datum.responses) {
        continue;
      }

      let time = moment(datum.time);
      let timeString = time.format(timeFormat);
      for (var k = 0; k < datum.responses.length; k++) {
        response = datum.responses[k];
        unit = response.data["Unit"];
        benchmarks = this.getBenchmarksFromResponse(time, response);
        data.push([
          datum.id,
          timeString,
          datum.location,
          datum.latitude,
          datum.longitude,
          unit,
          benchmarks["assigned"],
          benchmarks["responseTime"],
          benchmarks["arrivalTime"],
          benchmarks["sceneCleared"],
          benchmarks["turnoutTime"],
          benchmarks["travelTime"],
          benchmarks["timeOnScene"],
          benchmarks["committedHours"],
          incidents[i]
        ]);

        nextDataIndex++;
      }
    }

    this.setState({
      data: data,
      title: title
    });
  };

  getBenchmarksFromResponse = (time, response) => {
    let benchmarks = {};
    let benchmark, responseMinutes, travelMinutes;
    for (var i = 0; i < response.benchmarks.length; i++) {
      benchmark = response.benchmarks[i];
      if (benchmark.name == "Assigned") {
        benchmarks["assigned"] = moment(time)
          .add(benchmark.value, "minutes")
          .format(timeFormat);
      } else if (benchmark.name == "Responding") {
        responseMinutes = benchmark.value;
        benchmarks["responseTime"] = moment(time)
          .add(benchmark.value, "minutes")
          .format(timeFormat);
      } else if (benchmark.name == "TurnoutTime") {
        let minutes = Number.parseFloat(benchmark.value);
        if (!isNaN(minutes)) {
          benchmarks["turnoutTime"] = minutes.toFixed(2);
        }
      } else if (benchmark.name == "TravelTime") {
        travelMinutes = Number.parseFloat(benchmark.value);
        benchmarks["travelTime"] = travelMinutes.toFixed(2);
      } else if (benchmark.name == "ClearScene") {
        benchmarks["sceneCleared"] = moment(time)
          .add(benchmark.value, "minutes")
          .format(timeFormat);
      } else if (benchmark.name == "SceneTime") {
        let minutes = Number.parseFloat(benchmark.value);
        if (!isNaN(minutes)) {
          benchmarks["timeOnScene"] = minutes.toFixed(2);
        }
      } else if (benchmark.name == "CommittedHours") {
        let hours = Number.parseFloat(benchmark.value);
        if (!isNaN(hours)) {
          benchmarks["committedHours"] = hours.toFixed(2);
        }
      }
    }

    if (responseMinutes && travelMinutes) {
      benchmarks["arrivalTime"] = moment(time)
        .add(responseMinutes, "minutes")
        .add(travelMinutes, "minutes")
        .format(timeFormat);
    }

    return benchmarks;
  };

  render = () => {
    let page = 0;
    if (this.state.focusIndex) {
      page = Math.floor(this.state.focusIndex / 5);
    }

    if (this.tableRef.current) {
      this.tableRef.current.changePage(page);
    }

    const options = {
      page: page,
      filterType: "dropdown",
      responsive: "stacked",
      selectableRows: "none",
      rowsPerPage: 5,
      rowsPerPageOptions: [5, 10, 15],
      customRowRender: data => {
        const [
          id,
          time,
          location,
          latitude,
          longitude,
          unit,
          assigned,
          responseTime,
          arrivalTime,
          sceneCleared,
          turnoutTime,
          travelTime,
          timeOnScene,
          committedHours
        ] = data;

        return (
          <ChartDetailsRow
            id={id}
            time={time}
            location={location}
            latitude={latitude}
            longitude={longitude}
            unit={unit}
            assigned={assigned}
            responseTime={responseTime}
            arrivalTime={arrivalTime}
            sceneCleared={sceneCleared}
            turnoutTime={turnoutTime}
            travelTime={travelTime}
            timeOnScene={timeOnScene}
            committedHours={committedHours}
          />
        );
      }
    };

    return (
      <MUIDataTable
        innerRef={this.tableRef}
        title={this.state.title}
        data={this.state.data}
        columns={columns}
        options={options}
      />
    );
  };
}
ChartDetailsComponent.contextType = DataContext;
